// memoryGuardian.js — Onda 4 stage-1 (anti-travamento). Supervisor de pressão de
// memória do RENDERER.
//
// A causa raiz do "Apolo trava": o agent loop roda no MESMO renderer da UI, capado
// em 2GB (--max-old-space-size). Quando o heap chega no teto, o Chromium mata o
// renderer inteiro (render-process-gone reason=oom) → a janela morre. O fix
// estrutural completo é mover o loop pra outro processo (passo seguinte); este é o
// primeiro estágio do supervisor DENTRO da arquitetura atual: quando o heap se
// aproxima do teto, aborta um run de BACKGROUND (não observado) antes do OOM —
// shed de carga em vez de crash. A UI sobrevive.
//
// Política deliberada e conservadora:
//   - só age em pressão REAL (≥90% do jsHeapSizeLimit) — bem depois do uso normal;
//   - NUNCA aborta o run que o usuário está olhando (observed): mata só um run de
//     fundo, o mais antigo (o que mais acumulou); se não houver, só avisa;
//   - feature-detect: sem performance.memory (não-Chromium), vira no-op.
//
// As funções de decisão são PURAS (testáveis); o controller recebe tudo injetado.

export const HEAP_PRESSURE_PCT = 0.9; // 90% do teto do heap = zona de perigo pré-OOM

/**
 * Lê o heap JS do renderer. performance.memory é não-padrão (Chromium/Electron);
 * ausente → null (o guardian vira no-op).
 * @returns {{usedMB:number, limitMB:number}|null}
 */
export function readHeap(perf = typeof performance !== 'undefined' ? performance : undefined) {
  const m = perf && perf.memory;
  if (!m || !m.jsHeapSizeLimit) return null;
  return {
    usedMB: Math.round((m.usedJSHeapSize || 0) / 1048576),
    limitMB: Math.round(m.jsHeapSizeLimit / 1048576),
  };
}

/**
 * Está sob pressão? (used/limit ≥ pct). limit inválido → false (nunca age às cegas).
 * @param {number} usedMB
 * @param {number} limitMB
 * @param {number} [pct]
 * @returns {boolean}
 */
export function isUnderHeapPressure(usedMB, limitMB, pct = HEAP_PRESSURE_PCT) {
  if (!limitMB || limitMB <= 0) return false;
  return usedMB / limitMB >= pct;
}

// Pressão do SISTEMA (não só o heap do Apolo). Quando a MÁQUINA está crítica — por
// QUALQUER app, não só o Apolo — o guardião solta carga de fundo pra ser bom cidadão
// de memória. 15% disponível (via vm_stat, não freemem — ver electron/lib/systemMemory).
export const SYSTEM_PRESSURE_FREE_PCT = 0.15;

/**
 * Máquina sob pressão? (freePct < limiar). freePct inválido → false (nunca age às cegas).
 * @param {number} freePct  fração 0..1 de RAM disponível do total
 * @param {number} [pct]
 * @returns {boolean}
 */
export function isSystemUnderPressure(freePct, pct = SYSTEM_PRESSURE_FREE_PCT) {
  return typeof freePct === 'number' && freePct >= 0 && freePct < pct;
}

/**
 * Escolhe QUAL run abortar sob pressão. Só considera runs VIVOS e NÃO observados
 * (background); entre eles, o mais ANTIGO (menor startedAt = mais acumulou).
 * Devolve o id, ou null se não há candidato seguro (aí o controller só avisa).
 * @param {Array<{id:string, running:boolean, observed:boolean, startedAt:number|null}>} runs
 * @returns {string|null}
 */
export function pickSheddableRun(runs) {
  const list = Array.isArray(runs) ? runs : [];
  let pick = null;
  for (const r of list) {
    if (!r || !r.running || r.observed) continue; // nunca mata o que o usuário vê
    if (pick === null || (r.startedAt || 0) < (pick.startedAt || 0)) pick = r;
  }
  return pick ? pick.id : null;
}

/**
 * Inicia o supervisor. Poll a cada intervalMs: se sob pressão, tenta shedar um run
 * de background; senão avisa. Tudo injetado (testável, sem globals escondidos).
 * Retorna uma função de parada.
 * @param {object} deps
 * @param {() => {usedMB:number, limitMB:number}|null} deps.getHeap
 * @param {() => Array} deps.getRunningMeta   candidatos (ver pickSheddableRun)
 * @param {(id:string) => void} deps.shedRun  aborta+dropa o run (evictRun)
 * @param {(id:string) => void} [deps.onShed] efeito colateral de UI (msg de sistema)
 * @param {(info:{usedMB:number,limitMB:number}) => void} [deps.onWarn]
 * @param {() => boolean} [deps.isEnabled]    kill-switch (default: ligado)
 * @param {number} [deps.intervalMs]
 * @param {(fn:()=>void, ms:number)=>any} [deps.setIntervalFn]
 * @param {(t:any)=>void} [deps.clearIntervalFn]
 * @returns {() => void} stop
 */
export function startMemoryGuardian({
  getHeap,
  getRunningMeta,
  shedRun,
  getSystem = null, // opcional: () => Promise<{freePct}>|{freePct}|null — pressão do SISTEMA
  onShed = () => {},
  onWarn = () => {},
  isEnabled = () => true,
  intervalMs = 5000,
  setIntervalFn = setInterval,
  clearIntervalFn = clearInterval,
}) {
  // Sob pressão: solta um run de fundo (não observado); se não há, só avisa.
  // `info` carrega a razão ('heap'|'system') pro log/telemetria do caller.
  const shedOrWarn = (info) => {
    const id = pickSheddableRun(getRunningMeta());
    if (id) {
      shedRun(id);
      onShed(id, info);
    } else {
      onWarn(info);
    }
  };
  const tick = async () => {
    try {
      if (!isEnabled()) return;
      // 1) Heap do Apolo (SÍNCRONO — caminho original, sem await antes do shed).
      const heap = getHeap();
      if (heap && isUnderHeapPressure(heap.usedMB, heap.limitMB)) {
        shedOrWarn({ reason: 'heap', ...heap });
        return;
      }
      // 2) RAM do SISTEMA (assíncrono, opcional). Apolo cede memória quando a
      //    MÁQUINA está crítica, mesmo não sendo ele o vilão.
      if (getSystem) {
        const sys = await getSystem();
        if (sys && isSystemUnderPressure(sys.freePct)) {
          shedOrWarn({ reason: 'system', ...sys });
        }
      }
    } catch {
      /* best-effort — o guardião nunca pode derrubar o app */
    }
  };
  const timer = setIntervalFn(tick, intervalMs);
  if (timer && typeof timer.unref === 'function') timer.unref();
  return () => clearIntervalFn(timer);
}
